<template>
	<div class="height-100">
		<AppTabs :hideSelector="isCodeBlock || isTransactionalEmails" :tabItems="tabItems" />
	</div>
</template>
<script>
import TableViewTab from './tabs/TableViewTab';
import TestResultsTab from './tabs/TestResultsTab';
import { AI_DEFAULT_MAX_WORDS } from '@/components/BlockEditorWidget/widgetFieldTypes.const';
import AiAssistantTab from '@/components/CodeBlockAiTabs/AiAssistantTab.vue';
import TransactionalEmailTabDataProvider from '@/components/TransactionalEmailTabs/TransactionalEmailTabDataProvider.vue';
import { createAssistantThread } from '@/store/modules/aiAssistantApiDocs/actions';
import { updateNodeData } from '@/store/modules/nodes/actions';
import { getNode } from '@/store/modules/nodes/getters';
import { setParamInEditNode } from '@/store/modules/selectedNode/actions';
import { flatPreviousSteps, editNode } from '@/store/modules/selectedNode/getters';
import { getContent } from '@/store/modules/sideBarDrawer/getters';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import { buildKeyValObject } from '@/utils/helpers';

export default {
	name: 'SideBarDrawerContent',
	props: { isCodeBlock: { type: Boolean }, isTransactionalEmails: { type: Boolean } },
	computed: {
		...mapGettersShorthand({ getContent, editNode, getNode, flatPreviousSteps }),
		maxWords() {
			return { enabled: true, value: AI_DEFAULT_MAX_WORDS }; // If this is shared between here and CodeBlockParamType.vue, should move to use same const?
		},
		paramsMap() {
			return this.editNode?.params_map;
		},
		nodeFromGetNode() {
			return this.getNode(this.editNode?.id);
		},
		threadId() {
			return this.nodeFromGetNode.ai_assistant_thread_id;
		},
		selectedApiDocs: {
			get() {
				return this.paramsMap?.code_block?.selected_api_docs;
			},
			set(selectedApiDocs) {
				this.setParamInEditNode({
					key: 'params_map',
					value: { ...this.paramsMap, code_block: { ...this.paramsMap.code_block, selected_api_docs: selectedApiDocs } },
				});
			},
		},
		keyValObject() {
			return buildKeyValObject(this.paramsMap?.key_val);
		},
		aiAssistantProps() {
			return {
				selectedApiDocs: this.selectedApiDocs,
				keyValObject: this.keyValObject,
				threadId: this.threadId,
				isCodeblock: true,
				allowFineTuningValue: true,
				isRuntime: true,
				maxWords: this.maxWords,
				isReadOnly: false,
				autoSubmitPrefilled: false,
				placeholder: { enabled: false },
				promptPrefix: { enabled: false },
			};
		},
		transactionalEmailTabs() {
			return [
				{
					tab: 'Send an email',
					title: 'Send an email',
					disabled: false,
					tabIconWidth: '14',
					tabIconHeight: '14',
					component: TransactionalEmailTabDataProvider,
					props: {},
					listeners: {},
				},
			];
		},
		codeBlockTabs() {
			return [
				{
					tab: 'Code',
					title: 'Code',
					disabled: false,
					tabIconWidth: '14',
					tabIconHeight: '14',
					component: AiAssistantTab,
					props: { aiAssistantProps: this.aiAssistantProps },
					listeners: {
						'use-code-clicked': (val) => {
							const codeBlockVal = this.paramsMap.code_block || {};
							this.setParamInEditNode({
								key: 'params_map',
								value: { ...this.paramsMap, code_block: { ...codeBlockVal, code_block_val: val } },
							});
						},
						'reset-conversation': async (nodeId) => {
							const { thread_id: threadId } = await this.createAssistantThread({ nodeId });
							this.selectedApiDocs = { id: null, service_name: null };
							const nodeBeforeUpdate = this.getNode(this.editNode?.id);
							const updatedNode = { ...nodeBeforeUpdate, ai_assistant_thread_id: threadId };
							await this.updateNodeData({ data: updatedNode });
						},
						'api-docs-changed': ({ selectedApiDocsId, selectedApiDocName }) => {
							this.selectedApiDocs = { id: selectedApiDocsId, service_name: selectedApiDocName };
						},
					},
				},
			];
		},
		drawerContent() {
			return this.getContent || {};
		},
		drawerContentTablesTitles() {
			return this.drawerContent.tableViewer ? Object.keys(this.drawerContent.tableViewer) : [];
		},

		tabItems() {
			if (this.isTransactionalEmails) {
				return this.transactionalEmailTabs;
			}
			if (this.isCodeBlock) {
				return this.codeBlockTabs;
			}
			const tabs = [];
			// Source Tab
			for (const tableTab of this.drawerContentTablesTitles) {
				tabs.push({
					tab: tableTab,
					title: tableTab,
					component: TableViewTab,
					props: { drawerContent: this.drawerContent, titleKey: tableTab },
				});
			}
			// Results Tab
			tabs.push({
				tab: 'test_results',
				title: 'Test Results',
				component: TestResultsTab,
				props: { drawerContent: this.drawerContent, titleKey: 'test_results' },
			});
			return tabs;
		},
	},
	methods: { ...mapActionsShorthand({ setParamInEditNode, createAssistantThread, updateNodeData }) },
};
</script>

<style scoped lang="scss"></style>
